import type {
	BrandIdentity,
	ImageUrl,
} from '../../../../../bridge/types/business-profile';

// Single source of truth for the presence-check + latest_posts fallback.
// Both the read-view derivation and the edit seed go through this, so the
// "image_urls: [] must NOT fall back to latest_posts" rule is guarded once.
export function resolveImageryEntries(
	brandIdentity: BrandIdentity | undefined,
): ImageUrl[] {
	if (brandIdentity?.image_urls) {
		return brandIdentity.image_urls;
	}
	return (brandIdentity?.latest_posts?.value ?? []).map((p) => ({
		url: p.display_url,
		alt_text: '',
		source: 'instagram',
	}));
}

export function resolveImageryUrls(
	brandIdentity: BrandIdentity | undefined,
): string[] {
	return resolveImageryEntries(brandIdentity).map((img) => img.url);
}

/**
 * Folds a vision-tag result into the pending imagery draft, matched by url
 * (ADO-1268). The caller's own save then carries the tags, so tagging never
 * becomes a second writer racing that save.
 *
 * `tagged === null` means the request never reached the server, and the draft is
 * returned untouched: `tagging_status` stays unset so the entry remains eligible
 * for the server-side backfill. Writing `"failed"` there would be permanent —
 * nothing ever resets the field — and would exclude the photo from the retry
 * that would otherwise fix it. A server-reported `"failed"` IS persisted, since
 * the vision call was genuinely attempted.
 *
 * A url no longer in the draft (removed, or the card was cancelled and reseeded)
 * matches nothing, so a late result is a no-op.
 */
export function applyImageTagsToDraft(
	draft: ImageUrl[],
	url: string,
	tagged: { tags: string[]; alt_text: string; tagging_status: string } | null,
): ImageUrl[] {
	if (!tagged) return draft;
	return draft.map((entry) =>
		entry.url === url
			? {
					...entry,
					tags: tagged.tags,
					alt_text: tagged.alt_text || entry.alt_text,
					tagging_status: tagged.tagging_status,
				}
			: entry,
	);
}
